import { useState } from "react";
import { toast } from "sonner";
import { BeatShell, NumberField } from "@/components/nutrition/NutritionParts";
import { SyringeVial } from "@/components/visuals/SyringeVial";
import { gradeNumber } from "../beatUtils";
import { Briefing } from "./Shared";
import { Syringe, Check, X, Pill } from "lucide-react";

const fmt = (v) => (v == null || isNaN(v) ? "—" : parseFloat(Number(v).toFixed(3)).toString());

// Crash-trolley beat: pick the correct emergency drug and draw up the right
// volume. Graded half on drug choice, half on the calculated draw-up volume.
export function CrashTrolleyBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const full = beat.full ?? 20;
  const half = Math.round(full / 2);
  const correctDrug = beat.drugs.find((d) => d.correct);
  const correctVol = (beat.weight * correctDrug.dose) / correctDrug.conc;

  const [pick, setPick] = useState(null);
  const [vol, setVol] = useState("");
  const [res, setRes] = useState(null);
  const done = !!res;
  const picked = beat.drugs.find((d) => d.id === pick);

  const onPrimary = () => {
    if (!done) {
      if (pick == null) return toast.error("Select a drug from the trolley.");
      if (vol === "") return toast.error("Draw up a volume into the syringe.");
      const drugOk = pick === correctDrug.id;
      const volRes = gradeNumber(vol, correctVol, half);
      setRes({
        earned: (drugOk ? half : 0) + volRes.pts,
        max: full,
        detail: `${drugOk ? "Correct drug" : "Wrong drug"} · drew ${vol} mL (target ${fmt(correctVol)} mL)`,
        key: beat.key,
        label: beat.label,
        drugOk,
        volRes,
      });
    } else {
      onDone({ earned: res.earned, max: res.max, detail: res.detail, key: res.key, label: res.label });
    }
  };

  return (
    <BeatShell
      Icon={Syringe}
      eyebrow={eyebrow}
      title={beat.title}
      subtitle={beat.prompt}
      primaryLabel={done ? (beat.last ? "See debrief →" : "Continue →") : "Check the draw-up"}
      onPrimary={onPrimary}
      primaryTestId={`${beat.key}-primary-btn`}
      accent={accent}
    >
      {showBriefing && <Briefing c={c} />}

      {/* crash trolley drawer */}
      <div className="rounded-lg border-2 border-slate-300 bg-gradient-to-b from-slate-100 to-slate-200 p-3" data-testid="crash-trolley">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
          <Pill className="w-3.5 h-3.5" /> Crash trolley · drug drawer · patient {beat.weight} kg
        </div>
        <div className="grid grid-cols-2 gap-2">
          {beat.drugs.map((d) => {
            const selected = pick === d.id;
            let cls = "border-slate-300 bg-white hover:border-[#0047AB]/60";
            if (done) {
              if (d.correct) cls = "border-emerald-400 bg-emerald-50";
              else if (selected) cls = "border-rose-400 bg-rose-50";
              else cls = "border-slate-200 bg-white opacity-60";
            } else if (selected) {
              cls = "border-[#0047AB] bg-[#0047AB]/5 ring-1 ring-[#0047AB]/30";
            }
            return (
              <button
                key={d.id}
                type="button"
                disabled={done}
                onClick={() => setPick(d.id)}
                data-testid={`crash-drug-${d.id}`}
                className={`text-left border-2 rounded-md p-2.5 transition-all ${cls}`}
              >
                <div className="flex items-start justify-between gap-1">
                  <span className="text-[13px] font-bold text-slate-900 leading-tight">{d.name}</span>
                  {done && (d.correct ? <Check className="w-4 h-4 text-emerald-600 shrink-0" /> : selected ? <X className="w-4 h-4 text-rose-500 shrink-0" /> : null)}
                </div>
                <div className="text-[10px] font-mono-clinical text-slate-500 mt-0.5">{d.conc} mg/mL</div>
                {done && d.note && (
                  <div className={`text-[10px] mt-1 leading-snug ${d.correct ? "text-emerald-700" : "text-slate-500"}`}>{d.note}</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* dosing helper for the picked drug */}
      {picked && !done && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="crash-dose-hint">
          <span className="font-semibold text-slate-900">{picked.name}</span> — dose {picked.dose} mg/kg · concentration {picked.conc} mg/mL.
          <div className="text-[12px] text-slate-500 mt-1 font-mono-clinical">
            volume (mL) = weight × dose ÷ concentration = {beat.weight} × {picked.dose} ÷ {picked.conc}
          </div>
        </div>
      )}

      {/* syringe draw-up */}
      <SyringeVial volumeMl={parseFloat(vol) || 0} correctMl={correctVol} revealed={done} />
      <NumberField
        testid="crash-volume"
        label="Draw-up volume"
        unit="mL"
        hint={`Calculate the dose for the drug you selected · patient ${beat.weight} kg`}
        value={vol}
        onChange={setVol}
        disabled={done}
        result={done ? res.volRes : null}
        correctText={`${fmt(correctVol)} mL of ${correctDrug.name}`}
      />
    </BeatShell>
  );
}
